import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import AdminLayout from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Send, Clock, Trash2, Eye, Settings } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminBalanceCertPage() {
  const { t } = useLanguage();
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [schedule, setSchedule] = useState({ enabled: false, day_of_week: 'friday', hour: 9, minute: 0 });
  const [showSettings, setShowSettings] = useState(false);
  const [viewCert, setViewCert] = useState(null);

  const fetchCerts = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/balance-certificates`);
      setCerts(res.data);
    } catch { /* */ } finally { setLoading(false); }
  }, []);

  const fetchSchedule = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/balance-certificates/schedule`);
      setSchedule(res.data);
    } catch { /* */ }
  }, []);

  useEffect(() => { fetchCerts(); fetchSchedule(); }, [fetchCerts, fetchSchedule]);

  const handleSendAll = async () => {
    if (!window.confirm(t('bal_cert_send_all_confirm'))) return;
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/balance-certificates/send-now`);
      toast.success(t('bal_cert_sent').replace('{count}', res.data.sent));
      fetchCerts();
    } catch { toast.error(t('bal_cert_send_failed')); } finally { setSending(false); }
  };

  const handleSaveSchedule = async () => {
    try {
      await axios.put(`${API_URL}/api/admin/balance-certificates/schedule`, schedule);
      toast.success(t('bal_cert_schedule_saved'));
      setShowSettings(false);
    } catch { toast.error(t('error_occurred')); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('bal_cert_delete_confirm'))) return;
    try {
      await axios.delete(`${API_URL}/api/admin/balance-certificates/${id}`);
      setCerts(prev => prev.filter(c => c.id !== id));
      toast.success(t('admin_aml_deleted'));
    } catch { toast.error(t('aml_delete_failed')); }
  };

  const days = [
    { value: 'monday', label: t('day_monday') },
    { value: 'tuesday', label: t('day_tuesday') },
    { value: 'wednesday', label: t('day_wednesday') },
    { value: 'thursday', label: t('day_thursday') },
    { value: 'friday', label: t('day_friday') },
    { value: 'saturday', label: t('day_saturday') },
    { value: 'sunday', label: t('day_sunday') },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold" data-testid="bal-cert-title">{t('bal_cert_title')}</h1>
            <p className="text-muted-foreground text-sm">{t('bal_cert_subtitle')}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowSettings(true)} data-testid="bal-cert-settings-btn">
              <Settings className="h-4 w-4 mr-2" />{t('bal_cert_schedule_settings')}
            </Button>
            <Button onClick={handleSendAll} disabled={sending} data-testid="bal-cert-send-all-btn">
              <Send className="h-4 w-4 mr-2" />{sending ? t('saving') : t('bal_cert_send_all')}
            </Button>
          </div>
        </div>

        {/* Schedule status */}
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Clock className="h-5 w-5 text-muted-foreground" />
            <div>
              <div className="text-sm font-medium">{t('bal_cert_auto_schedule')}</div>
              <div className="text-xs text-muted-foreground">
                {schedule.enabled
                  ? `${days.find(d => d.value === schedule.day_of_week)?.label || schedule.day_of_week} ${String(schedule.hour).padStart(2, '0')}:${String(schedule.minute).padStart(2, '0')}`
                  : t('bal_cert_schedule_disabled')}
              </div>
            </div>
            <div className={`ml-auto px-2.5 py-0.5 rounded-full text-xs font-medium ${schedule.enabled ? 'bg-emerald-500/15 text-emerald-700' : 'bg-gray-500/15 text-gray-500'}`}>
              {schedule.enabled ? 'ON' : 'OFF'}
            </div>
          </div>
        </Card>

        {/* Certificates list */}
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="bal-cert-table">
              <thead className="bg-muted/30 border-b border-border">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">{t('bal_cert_col_date')}</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">{t('bal_cert_col_user')}</th>
                  <th className="text-right px-6 py-4 text-sm font-medium text-muted-foreground">{t('bal_cert_col_balance')}</th>
                  <th className="text-center px-6 py-4 text-sm font-medium text-muted-foreground">{t('bal_cert_col_email_status')}</th>
                  <th className="text-center px-6 py-4 text-sm font-medium text-muted-foreground">{t('bal_cert_col_type')}</th>
                  <th className="text-center px-6 py-4 text-sm font-medium text-muted-foreground">{t('admin_ref_actions_col')}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="px-6 py-12 text-center text-muted-foreground">{t('loading')}</td></tr>
                ) : certs.length === 0 ? (
                  <tr><td colSpan={6} className="px-6 py-12 text-center text-muted-foreground" data-testid="bal-cert-empty">{t('bal_cert_empty')}</td></tr>
                ) : certs.map(cert => (
                  <tr key={cert.id} className="border-b border-border/50 hover:bg-muted/20" data-testid={`bal-cert-row-${cert.id}`}>
                    <td className="px-6 py-4 text-sm">
                      {cert.issued_at ? new Date(cert.issued_at).toLocaleString('ja-JP') : '-'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium">{cert.user_name || '-'}</div>
                      <div className="text-xs text-muted-foreground">{cert.user_email}</div>
                    </td>
                    <td className="px-6 py-4 text-right font-mono font-semibold text-emerald-600">
                      ¥{(cert.balance || 0).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cert.email_sent ? 'bg-emerald-500/15 text-emerald-700' : 'bg-amber-500/15 text-amber-700'}`}>
                        {cert.email_sent ? t('bal_cert_sent_status') : t('bal_cert_unsent_status')}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-xs text-muted-foreground">{cert.schedule_type === 'scheduled' ? t('bal_cert_type_auto') : t('bal_cert_type_manual')}</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setViewCert(cert)} data-testid={`view-cert-${cert.id}`}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={() => handleDelete(cert.id)} data-testid={`delete-cert-${cert.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Schedule Settings Dialog */}
        <Dialog open={showSettings} onOpenChange={setShowSettings}>
          <DialogContent data-testid="bal-cert-schedule-dialog">
            <DialogHeader>
              <DialogTitle>{t('bal_cert_schedule_settings')}</DialogTitle>
              <DialogDescription>{t('bal_cert_schedule_desc')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-6 py-4">
              <div className="flex items-center justify-between">
                <Label>{t('bal_cert_auto_enabled')}</Label>
                <Switch checked={schedule.enabled} onCheckedChange={(v) => setSchedule({...schedule, enabled: v})} data-testid="schedule-enabled-switch" />
              </div>
              {schedule.enabled && (
                <>
                  <div className="space-y-2">
                    <Label>{t('bal_cert_day_of_week')}</Label>
                    <Select value={schedule.day_of_week} onValueChange={(v) => setSchedule({...schedule, day_of_week: v})}>
                      <SelectTrigger data-testid="schedule-day-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {days.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('bal_cert_hour')}</Label>
                      <Select value={String(schedule.hour)} onValueChange={(v) => setSchedule({...schedule, hour: parseInt(v)})}>
                        <SelectTrigger data-testid="schedule-hour-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Array.from({length: 24}, (_, i) => <SelectItem key={i} value={String(i)}>{String(i).padStart(2, '0')}:00</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>{t('bal_cert_minute')}</Label>
                      <Select value={String(schedule.minute)} onValueChange={(v) => setSchedule({...schedule, minute: parseInt(v)})}>
                        <SelectTrigger data-testid="schedule-minute-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {[0, 15, 30, 45].map(m => <SelectItem key={m} value={String(m)}>{String(m).padStart(2, '0')}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </>
              )}
              <Button className="w-full" onClick={handleSaveSchedule} data-testid="save-schedule-btn">
                {t('save')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* View Certificate Dialog */}
        <Dialog open={!!viewCert} onOpenChange={() => setViewCert(null)}>
          <DialogContent className="sm:max-w-lg" data-testid="view-cert-dialog">
            <DialogHeader>
              <DialogTitle>{t('bal_cert_detail_title')}</DialogTitle>
            </DialogHeader>
            {viewCert && (
              <div className="space-y-4 py-2">
                <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white p-6 rounded-xl">
                  <div className="text-xs opacity-60 mb-1">{t('bal_cert_col_balance')}</div>
                  <div className="text-3xl font-bold" data-testid="view-cert-balance">¥{(viewCert.balance || 0).toLocaleString()}</div>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-muted-foreground text-xs mb-1">{t('bal_cert_col_user')}</div>
                    <div className="font-medium">{viewCert.user_name || '-'}</div>
                    <div className="text-xs text-muted-foreground">{viewCert.user_email}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground text-xs mb-1">{t('bal_cert_col_date')}</div>
                    <div>{viewCert.issued_at ? new Date(viewCert.issued_at).toLocaleString('ja-JP') : '-'}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground text-xs mb-1">{t('bal_cert_col_email_status')}</div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${viewCert.email_sent ? 'bg-emerald-500/15 text-emerald-700' : 'bg-amber-500/15 text-amber-700'}`}>
                      {viewCert.email_sent ? t('bal_cert_sent_status') : t('bal_cert_unsent_status')}
                    </span>
                  </div>
                  <div>
                    <div className="text-muted-foreground text-xs mb-1">{t('bal_cert_col_type')}</div>
                    <div>{viewCert.schedule_type === 'scheduled' ? t('bal_cert_type_auto') : t('bal_cert_type_manual')}</div>
                  </div>
                </div>
                {viewCert.sent_at && (
                  <div className="text-xs text-muted-foreground border-t pt-3">
                    {t('bal_cert_sent_at')}: {new Date(viewCert.sent_at).toLocaleString('ja-JP')}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
